import { faArrowDown, faArrowUp, faEllipsis } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getMoveSectionTargetIndex, TMoveSectionDirection } from "redux/actions/websiteBuilder/editor/actions/section/moveSection";
import { clamp } from "lodash";
import { WEBSITE_BUILDER_Z_LAYERS } from "../../../../utility/websiteBuilderZLayers";
import { TWebsiteBuilderSection } from "../../../../nodes/section/websiteBuilderSection";
import { IWebsiteBuilderRoot } from "../../../../nodes/root/websiteBuilderRoot";
import { IWebsiteBuilderSectionOperations } from "../../../../hooks/nodeOperations/sectionOperations/useWebsiteBuilderSectionOperations";
import { IWebsiteBuilderContextMenuPosition } from "../../hooks/helpers/WebsiteBuilderContextMenu";
import { useAppSelector } from "hooks/redux";
import { useWebsiteBuilderEditorContext } from "../../../../websiteBuilderEditorContext";

interface ISectionToolbarProps {
  node: TWebsiteBuilderSection;
  root: IWebsiteBuilderRoot;
  hovered: boolean;
  sectionHeight: number;
  sectionOperations: IWebsiteBuilderSectionOperations;
  handleViewOpenContextMenu: (position: IWebsiteBuilderContextMenuPosition) => void;
}

interface ISectionToolbarSectionOuterPosition {
  type: "outer";
}

interface ISectionToolbarSectionInnerPosition {
  type: "inner";
  index: number;
}

type TSectionToolbarSectionPosition = ISectionToolbarSectionOuterPosition | ISectionToolbarSectionInnerPosition;

const TOOLBAR_SHRINK_AT_HEIGHT = 150;

export function SectionToolbar(props: ISectionToolbarProps) {
  const {
    node,
    root,
    hovered,
    sectionHeight,
    sectionOperations,
    handleViewOpenContextMenu,
  } = props;

  const { type: sectionType } = node.section;

  const { activePageDomNodeRefMap, scrollContainerRef } = useWebsiteBuilderEditorContext();

  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);
  const activePageId = useAppSelector(store => store.websiteBuilderStore.editor.activePageId);

  const toolbarRef = useRef<HTMLDivElement>(null);
  
  const [top, setTop] = useState<number>(null);
  
  const shrinkToolbar = sectionHeight <= TOOLBAR_SHRINK_AT_HEIGHT;

  const sectionPosition: TSectionToolbarSectionPosition = useMemo(() => {
    const outerSection = sectionType === "header" || sectionType === "footer";

    if (outerSection) {
      return {
        type: "outer",
      };
    } else {
      return {
        type: "inner",
        index: root.children.findIndex(child => child.id === node.id),
      };
    }
  }, [sectionType, root.children, node.id]);

  const { canMoveUp, canMoveDown } = useMemo(() => {
    if (sectionPosition.type === "outer") {
      return { canMoveUp: false, canMoveDown: false };
    }

    return {
      canMoveUp: getMoveSectionTargetIndex(sectionPosition.index, "up", root.children, deviceView) !== -1,
      canMoveDown: getMoveSectionTargetIndex(sectionPosition.index, "down", root.children, deviceView) !== -1,
    };
  }, [sectionPosition, root.children, deviceView]);

  const handleChangeToolbarPosition = useCallback(() => {
    const sectionRect = activePageDomNodeRefMap.current.get(node.id)?.getBoundingClientRect();
    const containerRect = scrollContainerRef.current?.getBoundingClientRect();
    const toolbarRect = toolbarRef.current?.getBoundingClientRect();

    if (!sectionRect || !containerRect || !toolbarRect) {
      return;
    }

    const screenPositionTop = sectionRect.top + sectionRect.height / 2 - toolbarRect.height / 2;
    const screenPositionBottom = screenPositionTop + toolbarRect.height;

    const containerBuffer = 8;

    const containerTop = containerRect.top + containerBuffer;
    const containerBottom = containerRect.bottom - containerBuffer;

    let updatedTop = sectionRect.height / 2 - toolbarRect.height / 2;

    if (screenPositionTop < containerTop) {
      const delta = containerTop - screenPositionTop;
      updatedTop += delta;
    } else if (screenPositionBottom > containerBottom) {
      const delta = screenPositionBottom - containerBottom;
      updatedTop -= delta;
    }

    updatedTop = clamp(updatedTop, containerBuffer, sectionRect.height - toolbarRect.height - containerBuffer);

    setTop(updatedTop);
  }, [node.id]);

  useEffect(() => {
    if (hovered) {
      scrollContainerRef.current?.addEventListener("scroll", handleChangeToolbarPosition);

      handleChangeToolbarPosition();
    }

    return () => {
      scrollContainerRef.current?.removeEventListener("scroll", handleChangeToolbarPosition);

      setTop(null);
    };
  }, [hovered, handleChangeToolbarPosition]);

  function handleMoveSection(direction: TMoveSectionDirection) {
    if (sectionPosition.type === "inner") {
      if (direction === "up") {
        sectionOperations.moveSectionUp(node, root, activePageId, deviceView, activePageDomNodeRefMap, scrollContainerRef);
      } else if (direction === "down") {
        sectionOperations.moveSectionDown(node, root, activePageId, deviceView, activePageDomNodeRefMap, scrollContainerRef);
      }
    }
  }

  return (
    <>
      {hovered && (
        <div
          ref={toolbarRef}
          className="website-builder-section-toolbar"
          style={{
            top: top != null ? `${top}px` : "50%",
            transform: `translate(calc(-50%), ${top != null ? "0" : "-50%"})`,
            zIndex: WEBSITE_BUILDER_Z_LAYERS.designSpace.sectionEditorUI,
          }}
        >
          {!shrinkToolbar && (
            <>
              <button
                onClick={() => handleMoveSection("up")}
                disabled={!canMoveUp}
              >
                <FontAwesomeIcon className="section-toolbar-option-icon" icon={faArrowUp} />
              </button>
              <button
                onClick={() => handleMoveSection("down")}
                disabled={!canMoveDown}
              >
                <FontAwesomeIcon className="section-toolbar-option-icon" icon={faArrowDown} />
              </button>
            </>
          )}
          <button onClick={e => handleViewOpenContextMenu({ x: e.clientX, y: e.clientY })}>
            <FontAwesomeIcon className="section-toolbar-option-icon" icon={faEllipsis} />
          </button>
        </div>
      )}
    </>
  );
}
